import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import {
  Send,
  CheckCircle2,
  AlertTriangle,
  SlidersHorizontal,
  MessageSquare,
  KeyRound,
  FileText,
  Headphones,
  Sparkles,
  Link2,
  Ticket,
  Share2,
  Languages,
  Plus,
  ArrowLeft,
  ChevronRight,
  Loader2,
  X,
  User,
  Mail,
  Phone,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SupportService from "../../services/support.service";
import ContactService from "../../services/contact.service";
import HealthService from "../../services/health.service";
import { useOrganization } from "../../contexts/OrganizationContext";
import AuthContext from "../../AuthContext";
import supportBot from "../../assets/support-bot.png";
import "./SupportView.css";

const DEFAULT_LANG = "en";

// Solo esto cambia con el selector de idioma del bot: el saludo y las
// recomendaciones. El resto de la app queda siempre en inglés. Después el
// usuario escribe en su idioma y el modelo (Sonnet 4.5) responde igual.
const SUPPORT_I18N = {
  en: {
    label: "English",
    greeting:
      "Hi 👋 I'm the Zenit support bot. Describe your issue and I'll help you resolve this case.",
    placeholder: "Type your message for support...",
    quickActions: [
      "View system status",
      "Open a ticket",
      "Connection issues",
      "Workflow help",
    ],
    topics: ["Reconnect Shopify", "Credential error", "Workflow stopped"],
  },
  es: {
    label: "Español",
    greeting:
      "Hola 👋 Soy el bot de soporte de Zenit. Cuéntame tu problema y te ayudo a resolver este caso.",
    placeholder: "Escribe tu mensaje para soporte...",
    quickActions: [
      "Ver estado del sistema",
      "Abrir ticket",
      "Problemas con conexiones",
      "Ayuda con workflows",
    ],
    topics: ["Reconectar Shopify", "Error en credenciales", "Workflow detenido"],
  },
  pt: {
    label: "Português",
    greeting:
      "Olá 👋 Sou o bot de suporte da Zenit. Conte-me seu problema e ajudo você a resolver este chamado.",
    placeholder: "Escreva sua mensagem para o suporte...",
    quickActions: [
      "Ver status do sistema",
      "Abrir chamado",
      "Problemas de conexão",
      "Ajuda com workflows",
    ],
    topics: ["Reconectar Shopify", "Erro de credenciais", "Workflow parado"],
  },
  fr: {
    label: "Français",
    greeting:
      "Bonjour 👋 Je suis le bot de support de Zenit. Décrivez votre problème et je vous aide à résoudre ce ticket.",
    placeholder: "Écrivez votre message au support...",
    quickActions: [
      "Voir l'état du système",
      "Ouvrir un ticket",
      "Problèmes de connexion",
      "Aide sur les workflows",
    ],
    topics: ["Reconnecter Shopify", "Erreur d'identifiants", "Workflow arrêté"],
  },
};

// App chrome — SIEMPRE en inglés (no cambia con el idioma del bot).
const UI = {
  header: "Support",
  subtitle:
    "Open a support case and chat with the Zenit bot to resolve technical questions, connection issues, and usage help.",
  casesTitle: "Your support cases",
  newCase: "New case",
  empty: "You have no support cases yet. Open one to start chatting.",
  back: "Cases",
  caseFallback: "Support case",
  messages: "messages",
  helpCenter: "Help center",
  botName: "Zenit support bot",
  available: "Available 24/7 to help you",
  online: "Online",
  systemStatus: "System status",
  operational: "Operational",
  operationalDesc: "All services are running normally.",
  statusChecking: "Checking status…",
  statusDegraded: "Some services are experiencing difficulties",
  offline: "Offline",
  checking: "Checking…",
  svcUp: "Operational",
  svcDown: "Down",
  statusAllUp: "All services operational",
  statusSomeDown: "Some services are down",
  frequentTopics: "Frequent topics",
  escalation: "Escalation",
  escalationDesc: "If the bot can't resolve your case, chat with a human agent.",
  talkHuman: "Talk to a human",
  modalDesc:
    "Leave your details and an agent will contact you. Your message goes straight to the Zenit team.",
  humanSent: "Request sent — the Zenit team will reach out shortly.",
  sending: "Sending…",
  done: "Done",
  name: "Name",
  phone: "Phone",
  email: "Email",
  message: "Message",
  namePh: "Your name",
  phonePh: "+506 0000 0000",
  emailPh: "you@email.com",
  messagePh: "Tell us how we can help...",
  cancel: "Cancel",
  send: "Send",
  close: "Close",
  botLangAria: "Bot language",
  errRequired: "Please fill in name, email, and message.",
  errEmail: "The email format is invalid.",
  notConfigured: "The support bot is not configured (missing API key).",
  sendFailed: "Couldn't send the message.",
  botTrouble: "Oops, I had trouble responding. Please try again in a moment.",
  handoffConfirm: "Contact the Zenit team with these details?",
  handoffYes: "Yes, contact",
  handoffNo: "No",
  handoffSent: "Request sent — the Zenit team will reach out.",
  handoffDeclined: "Okay, not sent.",
  handoffSending: "Sending…",
  handoffError: "Couldn't send. Please try again.",
};

const QUICK_ICONS = [Sparkles, Ticket, Link2, Share2];
const TOPIC_ICONS = [MessageSquare, KeyRound, FileText];

const formatWhen = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
};

// El bot, tras confirmar por texto, emite un bloque ```zenit:handoff {json}```.
// Lo parseamos: ocultamos el JSON y pintamos una tarjeta con botón que dispara el
// mailto pre-llenado. El bloque NUNCA se muestra crudo al usuario.
// Tolerante: acepta ```zenit:handoff```, ```json``` o un fence pelón, siempre que
// el JSON adentro tenga forma de handoff (name/email/message). Así, aunque el fence
// cambie al re-serializarse, la tarjeta se sigue mostrando en vez del JSON crudo.
const HANDOFF_RE = /```(?:zenit:handoff|json)?\s*(\{[\s\S]*?\})\s*```/i;

function looksLikeHandoff(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    ("email" in obj || "message" in obj) &&
    ("name" in obj || "email" in obj)
  );
}

function parseHandoff(text) {
  if (!text) return { cleanText: text, handoff: null };
  const match = text.match(HANDOFF_RE);
  if (!match) return { cleanText: text, handoff: null };
  let handoff = null;
  try {
    const parsed = JSON.parse(match[1].trim());
    if (looksLikeHandoff(parsed)) handoff = parsed;
  } catch {
    handoff = null;
  }
  // Solo tratamos el bloque como handoff (y lo quitamos) si tiene la forma esperada;
  // cualquier otro bloque de código se deja intacto para no romper mensajes normales.
  if (!handoff) return { cleanText: text, handoff: null };
  const cleanText = text.replace(HANDOFF_RE, "").trim();
  return { cleanText, handoff };
}

// Sends the handoff to the Zenit team via the AppSync `sendContactEmail`
// mutation (SES → contact@zenitailab.com). Returns the delivery result so
// the caller only shows "sent" when it actually went through.
function sendHandoff(handoff) {
  return ContactService.sendContactEmail({
    name: (handoff.name || "").trim(),
    email: (handoff.email || "").trim(),
    phone: (handoff.phone || "").trim(),
    reason: "Talk to a human",
    message: (handoff.message || "").trim() || "Support handoff request.",
  });
}

function HandoffCard({ handoff, state, onConfirm, onDecline }) {
  if (state === "sent") {
    return (
      <div className="support-handoff is-sent">
        <CheckCircle2 size={16} />
        <span>{UI.handoffSent}</span>
      </div>
    );
  }
  if (state === "declined") {
    return (
      <div className="support-handoff is-declined">
        <span>{UI.handoffDeclined}</span>
      </div>
    );
  }
  const { name, email, phone, message } = handoff;
  const sending = state === "sending";
  return (
    <div className="support-handoff">
      <div className="support-handoff-title">
        <Headphones size={16} />
        <span>{UI.handoffConfirm}</span>
      </div>
      <ul className="support-handoff-details">
        {name && (
          <li>
            <User size={13} /> {name}
          </li>
        )}
        {email && (
          <li>
            <Mail size={13} /> {email}
          </li>
        )}
        {phone && (
          <li>
            <Phone size={13} /> {phone}
          </li>
        )}
        {message && <li className="support-handoff-msg">“{message}”</li>}
      </ul>
      {state === "error" && (
        <div className="support-handoff-error">
          <AlertTriangle size={13} /> {UI.handoffError}
        </div>
      )}
      <div className="support-handoff-actions">
        <button
          type="button"
          className="support-handoff-yes"
          onClick={onConfirm}
          disabled={sending}
        >
          {sending ? (
            <Loader2 size={14} className="support-spin" />
          ) : (
            <Send size={14} />
          )}{" "}
          {sending ? UI.handoffSending : UI.handoffYes}
        </button>
        <button
          type="button"
          className="support-handoff-no"
          onClick={onDecline}
          disabled={sending}
        >
          {UI.handoffNo}
        </button>
      </div>
    </div>
  );
}

// Para "system status": el bot emite ```zenit:status {"services":[...]}``` y lo
// pintamos como tarjeta (puntitos verde/rojo) en vez de markdown suelto.
// Capturamos TODO entre los fences (no `\{...\}` no-greedy): el JSON de status tiene
// objetos anidados y un no-greedy se cortaría en el primer `}`.
const STATUS_RE = /```zenit:status\s*([\s\S]*?)```/i;

function parseStatus(text) {
  if (!text) return { cleanText: text, status: null };
  const match = text.match(STATUS_RE);
  if (!match) return { cleanText: text, status: null };
  let status = null;
  try {
    const parsed = JSON.parse(match[1].trim());
    if (parsed && Array.isArray(parsed.services)) status = parsed;
  } catch {
    status = null;
  }
  if (!status) return { cleanText: text, status: null };
  return { cleanText: text.replace(STATUS_RE, "").trim(), status };
}

function StatusCard({ status }) {
  const services = Array.isArray(status?.services) ? status.services : [];
  if (!services.length) return null;
  const allUp = services.every((s) => s.up);
  return (
    <div className={`support-statuscard ${allUp ? "is-up" : "is-down"}`}>
      <div className="support-statuscard-head">
        {allUp ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        <span>{allUp ? UI.statusAllUp : UI.statusSomeDown}</span>
      </div>
      <ul className="support-statuscard-list">
        {services.map((s, idx) => (
          <li key={idx} className={s.up ? "svc-up" : "svc-down"}>
            <span className="support-statuscard-dot" />
            <span className="support-statuscard-name">{s.name}</span>
            {s.detail && (
              <span className="support-statuscard-detail">{s.detail}</span>
            )}
            <span className="support-statuscard-state">
              {s.up ? UI.svcUp : UI.svcDown}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Los links que el bot incluye (a secciones de la app o docs) abren en PESTAÑA
// NUEVA, para que el usuario no pierda la conversación de soporte al ir a hacer algo.
const MD_COMPONENTS = {
  a: ({ href, children, ...props }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
};

const SupportView = ({ userId }) => {
  const { currentAccount } = useOrganization();
  const { user } = useContext(AuthContext);
  const accountId = currentAccount?.id;
  const [lang, setLang] = useState(DEFAULT_LANG);
  const t = SUPPORT_I18N[lang] || SUPPORT_I18N[DEFAULT_LANG];

  const [view, setView] = useState("list"); // 'list' | 'chat'
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [casesError, setCasesError] = useState(null);

  const [activeCase, setActiveCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // Acción tomada por cada tarjeta de handoff, keyed por índice de mensaje.
  const [handoffActions, setHandoffActions] = useState({});
  const scrollRef = useRef(null);

  // "Talk to a human" form
  const [showHuman, setShowHuman] = useState(false);
  const [human, setHuman] = useState({ name: "", phone: "", email: "", message: "" });
  const [humanErr, setHumanErr] = useState(null);
  // idle | sending | sent | error
  const [humanStatus, setHumanStatus] = useState("idle");

  // System status (health de los servicios)
  const [health, setHealth] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);

  const configured = SupportService.isConfigured;

  const loadCases = useCallback(async () => {
    if (!configured || !userId || !accountId) {
      setCasesLoading(false);
      return;
    }
    setCasesLoading(true);
    setCasesError(null);
    const res = await SupportService.listCases({ accountId });
    if (res.success) setCases(res.cases);
    else setCasesError(res.error);
    setCasesLoading(false);
  }, [configured, userId, accountId]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, view]);

  // Chequeo de salud de servicios al montar + re-poll cada 60s.
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const h = await HealthService.check();
      if (mounted) setHealth(h);
    };
    run();
    const id = setInterval(run, 60000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const openNewCase = () => {
    if (!configured) {
      setCasesError(UI.notConfigured);
      return;
    }
    // La conversación se crea en el backend con el primer mensaje (título por IA).
    // Aquí solo abrimos un caso "borrador" con id null.
    setActiveCase({ id: null, title: UI.caseFallback });
    setMessages([{ role: "assistant", text: t.greeting }]);
    setError(null);
    setInput("");
    setView("chat");
  };

  const openCase = async (c) => {
    setActiveCase({ id: c.id, title: c.title || UI.caseFallback });
    setError(null);
    setInput("");
    setView("chat");
    setMessagesLoading(true);
    const res = await SupportService.getMessages({ chatHistoryId: c.id });
    setMessagesLoading(false);
    if (res.success && res.messages.length) {
      setMessages(res.messages);
    } else {
      setMessages([{ role: "assistant", text: t.greeting }]);
    }
  };

  const backToList = () => {
    setView("list");
    setActiveCase(null);
    setMessages([]);
    loadCases();
  };

  const sendText = useCallback(async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || sending || !activeCase || !accountId) return;

    setError(null);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setSending(true);

    try {
      const res = await SupportService.sendMessage({
        accountId,
        userId,
        chatHistoryId: activeCase.id, // null en un caso nuevo -> el backend lo crea
        text,
      });
      if (!res.success) throw new Error(res.error);
      // Si era un caso nuevo, adoptar el id de conversación que devolvió el backend.
      if (!activeCase.id && res.chatHistoryId) {
        setActiveCase((prev) => ({ ...prev, id: res.chatHistoryId }));
      }
      setMessages((prev) => [...prev, { role: "assistant", text: res.reply || "…" }]);
    } catch (err) {
      setError(err.message || UI.sendFailed);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: UI.botTrouble, isError: true },
      ]);
    } finally {
      setSending(false);
    }
  }, [sending, activeCase, userId, accountId]);

  const handleSend = useCallback(() => sendText(input), [sendText, input]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openHumanForm = () => {
    setHumanErr(null);
    setHumanStatus("idle");
    // Prefill name/email (account) and phone (profile), like the "Get more
    // coins" dialog — but the message stays empty for the user to write.
    setHuman({
      name: user?.username || user?.name || currentAccount?.name || "",
      phone: user?.phone_number || user?.phone || "",
      email: user?.email || "",
      message: "",
    });
    setShowHuman(true);
  };

  const submitHumanForm = async () => {
    if (humanStatus === "sending") return;
    const name = human.name.trim();
    const email = human.email.trim();
    const message = human.message.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!name || !email || !message) {
      setHumanErr(UI.errRequired);
      return;
    }
    if (!emailOk) {
      setHumanErr(UI.errEmail);
      return;
    }

    setHumanErr(null);
    setHumanStatus("sending");
    const res = await ContactService.sendContactEmail({
      name,
      email,
      phone: human.phone,
      reason: "Talk to a human",
      message,
    });
    if (res.success) {
      setHumanStatus("sent");
    } else {
      setHumanErr(res.message || UI.sendFailed);
      setHumanStatus("error");
    }
  };

  return (
    <div className="support-view">
      <div className="support-head">
        <h1>{UI.header}</h1>
        <p>{UI.subtitle}</p>
      </div>

      <div className="support-layout">
        <section className="support-chat">
          {view === "list" ? (
            <>
              <div className="support-cases-head">
                <h2>{UI.casesTitle}</h2>
                <button
                  className="support-newcase"
                  onClick={openNewCase}
                  disabled={creating}
                  type="button"
                >
                  {creating ? (
                    <Loader2 size={16} className="support-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  <span>{UI.newCase}</span>
                </button>
              </div>

              {casesError && <p className="support-error">{casesError}</p>}

              <div className="support-cases-list">
                {casesLoading ? (
                  <div className="support-loading">
                    <Loader2 size={22} className="support-spin" />
                  </div>
                ) : cases.length === 0 ? (
                  <div className="support-empty">
                    <span className="support-empty-icon">
                      <Ticket size={26} />
                    </span>
                    <p>{UI.empty}</p>
                    <button
                      className="support-newcase"
                      onClick={openNewCase}
                      disabled={creating}
                      type="button"
                    >
                      <Plus size={16} />
                      <span>{UI.newCase}</span>
                    </button>
                  </div>
                ) : (
                  cases.map((c) => (
                    <button
                      key={c.id}
                      className="support-case-item"
                      onClick={() => openCase(c)}
                      type="button"
                    >
                      <span className="support-case-icon">
                        <Ticket size={17} />
                      </span>
                      <div className="support-case-body">
                        <span className="support-case-name">
                          {c.title || UI.caseFallback}
                        </span>
                        <span className="support-case-meta">
                          {formatWhen(c.updated_at || c.updatedAt || c.created_at)}
                          {" · "}
                          {c.total_messages ?? c.totalMessages ?? 0} {UI.messages}
                        </span>
                      </div>
                      <ChevronRight size={18} className="support-case-chevron" />
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="support-chat-topbar">
                <button className="support-back" onClick={backToList} type="button">
                  <ArrowLeft size={16} />
                  <span>{UI.back}</span>
                </button>
                <span className="support-case-title">
                  {activeCase?.title || UI.caseFallback}
                </span>
              </div>

              <div className="support-messages" ref={scrollRef}>
                {messagesLoading ? (
                  <div className="support-loading">
                    <Loader2 size={22} className="support-spin" />
                  </div>
                ) : (
                  messages.map((m, i) => (
                    <div
                      key={i}
                      className={`support-msg ${m.role === "user" ? "is-user" : "is-bot"}`}
                    >
                      {m.role === "assistant" && (
                        <span className="support-msg-avatar">
                          <img src={supportBot} alt="Support bot" />
                        </span>
                      )}
                      <div className={`support-bubble ${m.isError ? "is-error" : ""}`}>
                        {m.role === "user" ? (
                          m.text
                        ) : (
                          (() => {
                            let { cleanText, handoff } = parseHandoff(m.text);
                            let status = null;
                            if (!handoff) {
                              const s = parseStatus(cleanText);
                              status = s.status;
                              cleanText = s.cleanText;
                            }
                            return (
                              <>
                                {cleanText && (
                                  <div className="support-md">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={MD_COMPONENTS}
                                    >
                                      {cleanText}
                                    </ReactMarkdown>
                                  </div>
                                )}
                                {handoff && (
                                  <HandoffCard
                                    handoff={handoff}
                                    state={handoffActions[i]}
                                    onConfirm={async () => {
                                      setHandoffActions((s) => ({ ...s, [i]: "sending" }));
                                      const res = await sendHandoff(handoff);
                                      setHandoffActions((s) => ({
                                        ...s,
                                        [i]: res.success ? "sent" : "error",
                                      }));
                                    }}
                                    onDecline={() =>
                                      setHandoffActions((s) => ({ ...s, [i]: "declined" }))
                                    }
                                  />
                                )}
                                {status && <StatusCard status={status} />}
                              </>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  ))
                )}

                {sending && (
                  <div className="support-msg is-bot">
                    <span className="support-msg-avatar">
                      <img src={supportBot} alt="Support bot" />
                    </span>
                    <div className="support-bubble support-typing">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
              </div>

              <div className="support-quick-actions">
                {t.quickActions.map((label, i) => {
                  const Icon = QUICK_ICONS[i];
                  return (
                    <button
                      key={label}
                      className="support-chip"
                      onClick={() => sendText(label)}
                      disabled={sending}
                      type="button"
                    >
                      {Icon && <Icon size={15} />}
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              {error && <p className="support-error">{error}</p>}

              <div className="support-composer">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t.placeholder}
                  rows={1}
                  disabled={sending}
                />
                <button
                  className="support-send"
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  type="button"
                  aria-label={UI.send}
                >
                  <Send size={18} />
                </button>
              </div>
            </>
          )}
        </section>

        {/* Help center */}
        <aside className="support-sidebar">
          <div className="support-card support-bot-card">
            <div className="support-card-head">
              <Sparkles size={16} />
              <span>{UI.helpCenter}</span>
            </div>
            <div className="support-bot-avatar">
              <img src={supportBot} alt={UI.botName} />
            </div>
            <h3>{UI.botName}</h3>
            <p className="support-muted">{UI.available}</p>
            <span
              className={`support-online ${
                health === null ? "is-checking" : health.botUp ? "" : "is-down"
              }`}
            >
              <span className="support-dot" />{" "}
              {health === null ? UI.checking : health.botUp ? UI.online : UI.offline}
            </span>

            <label className="support-lang">
              <Languages size={15} />
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label={UI.botLangAria}
              >
                {Object.entries(SUPPORT_I18N).map(([code, cfg]) => (
                  <option key={code} value={code}>
                    {cfg.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="support-card">
            <div className="support-card-head">
              {health === null ? (
                <Loader2 size={16} className="support-spin" />
              ) : health.allUp ? (
                <CheckCircle2 size={16} className="ok" />
              ) : (
                <AlertTriangle size={16} className="warn" />
              )}
              <span>{UI.systemStatus}</span>
              {health && (
                <button
                  className={`support-status-toggle ${statusOpen ? "open" : ""}`}
                  onClick={() => setStatusOpen((o) => !o)}
                  type="button"
                  aria-label="Toggle service details"
                >
                  <ChevronRight size={16} />
                </button>
              )}
            </div>

            <div
              className={`support-status ${
                health === null ? "is-loading" : health.allUp ? "is-ok" : "is-warn"
              }`}
            >
              <div className="support-status-title">
                {health === null ? (
                  <Loader2 size={15} className="support-spin" />
                ) : health.allUp ? (
                  <CheckCircle2 size={15} className="ok" />
                ) : (
                  <AlertTriangle size={15} className="warn" />
                )}
                <span>
                  {health === null
                    ? UI.statusChecking
                    : health.allUp
                    ? UI.operational
                    : UI.statusDegraded}
                </span>
              </div>
              {health !== null && (
                <p className="support-muted">
                  {health.allUp
                    ? UI.operationalDesc
                    : `${health.down.map((d) => d.label).join(", ")} — degraded.`}
                </p>
              )}
            </div>

            {statusOpen && health && (
              <ul className="support-health-list">
                {health.services.map((s) => (
                  <li key={s.key}>
                    <span className={`support-health-dot ${s.up ? "up" : "down"}`} />
                    <span className="support-health-label">{s.label}</span>
                    <span className={`support-health-state ${s.up ? "up" : "down"}`}>
                      {s.up ? UI.svcUp : UI.svcDown}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="support-card">
            <div className="support-card-head">
              <SlidersHorizontal size={16} />
              <span>{UI.frequentTopics}</span>
            </div>
            <ul className="support-topics">
              {t.topics.map((label, i) => {
                const Icon = TOPIC_ICONS[i];
                return (
                  <li key={label}>
                    <span className="support-topic-icon">
                      {Icon && <Icon size={15} />}
                    </span>
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="support-card">
            <div className="support-card-head">
              <Headphones size={16} />
              <span>{UI.escalation}</span>
            </div>
            <p className="support-muted">{UI.escalationDesc}</p>
            <button className="support-human-btn" type="button" onClick={openHumanForm}>
              <Headphones size={16} />
              {UI.talkHuman}
            </button>
          </div>
        </aside>
      </div>

      {showHuman && (
        <div className="support-modal-overlay" onClick={() => setShowHuman(false)}>
          <div className="support-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="support-modal-close"
              onClick={() => setShowHuman(false)}
              aria-label={UI.close}
              type="button"
            >
              <X size={18} />
            </button>

            <div className="support-modal-head">
              <span className="support-modal-icon"><Headphones size={20} /></span>
              <div>
                <h3>{UI.talkHuman}</h3>
                <p>{UI.modalDesc}</p>
              </div>
            </div>

            {humanStatus === "sent" ? (
              <div className="support-human-sent">
                <CheckCircle2 size={22} />
                <p>{UI.humanSent}</p>
                <button
                  className="support-modal-send"
                  onClick={() => setShowHuman(false)}
                  type="button"
                >
                  {UI.done}
                </button>
              </div>
            ) : (
              <>
                <div className="support-field">
                  <label><User size={14} /> {UI.name}</label>
                  <input
                    type="text"
                    value={human.name}
                    onChange={(e) => setHuman((p) => ({ ...p, name: e.target.value }))}
                    placeholder={UI.namePh}
                  />
                </div>

                <div className="support-field-row">
                  <div className="support-field">
                    <label><Phone size={14} /> {UI.phone}</label>
                    <input
                      type="tel"
                      value={human.phone}
                      onChange={(e) => setHuman((p) => ({ ...p, phone: e.target.value }))}
                      placeholder={UI.phonePh}
                    />
                  </div>
                  <div className="support-field">
                    <label><Mail size={14} /> {UI.email}</label>
                    <input
                      type="email"
                      value={human.email}
                      onChange={(e) => setHuman((p) => ({ ...p, email: e.target.value }))}
                      placeholder={UI.emailPh}
                    />
                  </div>
                </div>

                <div className="support-field">
                  <label><MessageSquare size={14} /> {UI.message}</label>
                  <textarea
                    value={human.message}
                    onChange={(e) => setHuman((p) => ({ ...p, message: e.target.value }))}
                    placeholder={UI.messagePh}
                    rows={4}
                  />
                </div>

                {humanErr && <p className="support-error">{humanErr}</p>}

                <div className="support-modal-actions">
                  <button
                    className="support-modal-cancel"
                    onClick={() => setShowHuman(false)}
                    type="button"
                    disabled={humanStatus === "sending"}
                  >
                    {UI.cancel}
                  </button>
                  <button
                    className="support-modal-send"
                    onClick={submitHumanForm}
                    type="button"
                    disabled={humanStatus === "sending"}
                  >
                    {humanStatus === "sending" ? (
                      <Loader2 size={16} className="support-spin" />
                    ) : (
                      <Mail size={16} />
                    )}
                    {humanStatus === "sending" ? UI.sending : UI.send}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportView;
